import {
  DataTypeStore,
  DropdownType,
  getExceptionReasonsOptions,
  hideLoader,
  IEntityFieldType,
  showLoader,
  SiteSettingsType,
  WORKFLOW_UI_IDENTIFIER,
  WorkflowInstanceTaskType,
} from '@app-store'
import {
  TableColumnType,
  Accordion,
  Checkbox,
  CustomTable,
  FormInput,
  SvgReload,
  ControlledMultiSelect,
  TENANTS,
} from '@ui-kit'
import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  editEntityDataEmbeddedAPI,
  executeCustomProcedure,
  getCaseKeyCaseNameByEntity,
} from '../../apis/entityData.api'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import {
  NAMESPACES,
  PAP_WORKFLOW_CONSTANTS,
  WORKFLOW_STATUS,
} from '../../utils/constants'
import { useParams } from 'react-router-dom'
import { STRINGS } from '../../utils/strings'

export const EligibilityAssessmentResultsTask = ({
  task,
  uiIdentifierRef,
}: {
  task?: WorkflowInstanceTaskType
  uiIdentifierRef: React.MutableRefObject<{
    [key: string]: {
      updateCaseData?: () => Promise<boolean>
    }
  }>
}) => {
  const { caseId } = useParams()
  const selectedClient = useAppSelector(
    (state) => state?.authUser?.selectedClientTenant
  )
  const [outcomeData, setOutcomeData] = useState<DataTypeStore[]>([])
  const [showRequestForException, setShowRequestForException] = useState(true)
  const [showRequestForExceptionReason, setShowRequestForExceptionReason] =
    useState(false)
  const [isHiddenException, setIsHiddenException] = useState<boolean>(false)
  const [showDenialReason, setShowDenialReason] = useState<boolean>(false)
  const workflowInstance = useAppSelector(
    (state) => state?.workflowData?.workflowInstance
  )
  const workflowDefinition = useAppSelector(
    (state) => state?.workflowData?.workflowDefinition
  )
  const siteSettings = useAppSelector((state) => state?.authUser?.site_settings)
  const dispatch = useAppDispatch()
  const formState: {
    id?: string
    enrollment_type?: string
    enrollment_subtype?: string
    enrollment_reason?: string
    decision?: string
    decision_reason?: string
    denial_reason?: string
    request_for_exception?: boolean
    request_for_exception_reason?: string
    exception_reason?: string[]
  } = {}
  const { setValue, register, watch, control } = useForm({
    defaultValues: formState,
  })

  const [exceptionReasons, setexceptionReasons] = useState<
    DropdownType<string>[]
  >([])

  const columns: TableColumnType<DataTypeStore>[] = [
    {
      header: 'Criteria',
      accessor: 'criteria',
      dataType: 'string',
    },
    {
      header: 'Input',
      accessor: 'input',
      type: 'component',
      renderComponent: (row: DataTypeStore) => (
        <div className="font-light">
          {Array.isArray(row?.input)
            ? row?.input?.map((item: string | number, index: number) => (
                <div key={`${item}_${index}`}>{item}</div>
              ))
            : ''}
        </div>
      ),
    },
    {
      header: 'Outcome',
      accessor: 'outcome',
    },
    {
      header: 'Outcome Reason',
      accessor: 'outcome_reason',
    },
  ]
  const layout: {
    name:
      | 'enrollment_type'
      | 'enrollment_subtype'
      | 'enrollment_reason'
      | 'decision'
      | 'decision_reason'
      | 'denial_reason'
      | 'request_for_exception'
      | 'exception_reason'
      | 'request_for_exception_reason'
    label: string
    type?: string
    row?: number
    colSpan?: number
    required?: boolean
    visible?: boolean
    isEditable?: boolean
  }[] = [
    ...(task?.identifier?.ui_identifier ===
      WORKFLOW_UI_IDENTIFIER.VOUCHER_ACCESS_ELIGIBILITY &&
    selectedClient?.id === TENANTS.NOVO
      ? []
      : [
          {
            name: PAP_WORKFLOW_CONSTANTS.ENROLLMENT_TYPE_KEY,
            label: PAP_WORKFLOW_CONSTANTS.ENROLLMENT_TYPE_LABEL,
            required: true,
          },
          {
            name: PAP_WORKFLOW_CONSTANTS.ENROLLMENT_SUBTYPE_KEY,
            label: PAP_WORKFLOW_CONSTANTS.ENROLLMENT_SUBTYPE_LABEL,
            required: true,
          },
        ]),
    {
      name: PAP_WORKFLOW_CONSTANTS?.DECISION_KEY,
      label: PAP_WORKFLOW_CONSTANTS?.DECISION_LABEL,
      required: true,
    },
    {
      name: PAP_WORKFLOW_CONSTANTS?.DECISION_REASON_KEY,
      label: PAP_WORKFLOW_CONSTANTS?.DECISION_REASON_LABEL,
      required: true,
    },
    {
      name: PAP_WORKFLOW_CONSTANTS?.REQUEST_FOR_EXCEPTION_KEY,
      label: PAP_WORKFLOW_CONSTANTS?.REQUEST_FOR_EXCEPTION_LABEL,
      type: 'checkbox',
      row: 3,
      visible: showRequestForException && !isHiddenException,
      isEditable: task?.state === WORKFLOW_STATUS.OPEN,
    },
    {
      name: PAP_WORKFLOW_CONSTANTS?.EXCEPTION_REASON_KEY,
      label: PAP_WORKFLOW_CONSTANTS?.EXCEPTION_REASON_LABEL,
      type: 'MultiSelectDropdown',
      row: 3,
      visible: showRequestForException && showRequestForExceptionReason,
      isEditable: task?.state === WORKFLOW_STATUS.OPEN,
    },
    {
      name: PAP_WORKFLOW_CONSTANTS?.REQUEST_FOR_EXCEPTION_REASON_KEY,
      label: PAP_WORKFLOW_CONSTANTS?.REQUEST_FOR_EXCEPTION_REASON_LABEL,
      row: 4,
      colSpan: 3,
      visible: showRequestForException && showRequestForExceptionReason,
      isEditable: task?.state === WORKFLOW_STATUS.OPEN,
    },
    {
      name: PAP_WORKFLOW_CONSTANTS.DENIAL_REASON_KEY,
      label: PAP_WORKFLOW_CONSTANTS.DENIAL_REASON_LABEL,
      visible: showDenialReason,
    },
  ]
  const fetchOutcomeDetails = async () => {
    try {
      const payload = {
        entity_name: task?.identifier?.entity_name,
        select: [
          'id',
          'case',
          'c_pap_eligibility_assessment_details_eligibility_assessment',
          'pap_enrollment_sub_type',
          'pap_enrollment_type',
          'status',
          'decision',
          'decision_reason',
          'request_for_exception',
          'request_for_exception_reason',
          'exception_reason',
        ],
        related_entities: {
          c_pap_eligibility_assessment_details_eligibility_assessment: {
            select: [
              'id',
              'eligibility_config',
              'output_json',
              'eligibility_outcome',
              'eligibility_outcome_reason',
              'exception_reason',
            ],
            related_entities: {
              eligibility_config: {
                select: ['id', 'eligibility_rule'],
                related_entities: {
                  eligibility_rule: {
                    select: ['id', 'rule_label'],
                  },
                },
              },
            },
          },
        },
      }
      const response = await getCaseKeyCaseNameByEntity(
        payload,
        task?.identifier?.entity_name ?? '',
        (Number(task?.resource_id) ?? 0).toString()
      )

      const denialReasonConfig =
        (
          response?.data
            ?.c_pap_eligibility_assessment_details_eligibility_assessment ?? []
        )
          ?.filter(
            (item: { eligibility_outcome: { name: string } }) =>
              item.eligibility_outcome.name ===
                PAP_WORKFLOW_CONSTANTS.NOT_ELIGIBLE ||
              item.eligibility_outcome.name ===
                PAP_WORKFLOW_CONSTANTS.UNDETERMINED
          )
          ?.map(
            (item: {
              eligibility_config: {
                eligibility_rule: { rule_label: string }
              }
            }) => item.eligibility_config.eligibility_rule.rule_label
          )
          ?.join(', ') ?? ''

      setValue('id', response.data?.id?.toString() ?? '')
      setValue('decision', response.data?.decision?.name ?? '')
      if (response.data?.decision?.name === PAP_WORKFLOW_CONSTANTS.DENIED) {
        setShowDenialReason(true)
      }
      setValue('decision_reason', response.data?.decision_reason?.name ?? '')
      setValue('denial_reason', denialReasonConfig)
      setValue(
        'enrollment_type',
        response.data?.pap_enrollment_type?.name ?? ''
      )
      setValue(
        'enrollment_subtype',
        response.data?.pap_enrollment_sub_type?.name ?? ''
      )
      setValue(
        'request_for_exception',
        response.data?.request_for_exception ?? false
      )
      setValue('exception_reason', response.data?.exception_reason)
      if (response.data?.request_for_exception)
        setShowRequestForExceptionReason(true)
      setValue(
        'request_for_exception_reason',
        response.data?.request_for_exception_reason ?? ''
      )
      if (
        (response.data?.decision?.name ?? '').toLocaleLowerCase() ===
        NAMESPACES.APPROVED.toLocaleLowerCase()
      ) {
        setShowRequestForException(false)
      } else {
        setShowRequestForException(true)
      }
      const data: DataTypeStore[] = []
      response.data?.c_pap_eligibility_assessment_details_eligibility_assessment.forEach(
        (item: {
          eligibility_outcome: { name: string }
          eligibility_outcome_reason: { name: string }
          eligibility_config: {
            eligibility_rule: { rule_label: string }
          }
          status: string
          output_json: {
            label: string
            value: string
            diagnosis_code_type: string
          }[]
        }) => {
          data.push({
            criteria: item?.eligibility_config?.eligibility_rule?.rule_label,
            outcome: item?.eligibility_outcome?.name,
            outcome_reason: item?.eligibility_outcome_reason?.name,
            input: Array.isArray(item?.output_json)
              ? item?.output_json?.map(
                  (dataItem: {
                    label: string
                    value: string
                    diagnosis_code_type: string
                  }) => {
                    if (
                      item?.eligibility_config?.eligibility_rule?.rule_label ===
                      NAMESPACES.DIAGNOSIS
                    ) {
                      if (
                        dataItem.diagnosis_code_type ===
                        NAMESPACES.OFF_LABEL_HISTORICAL_DENY
                      )
                        setShowRequestForException(false)
                    }
                    if (dataItem.label)
                      return dataItem.label + ': ' + dataItem.value
                    else return dataItem.value
                  }
                )
              : [],
          })
        }
      )
      setOutcomeData(data)
    } catch (err) {
      console.error(err)
    }
  }

  const updateCaseDetails = async () => {
    if (watch('request_for_exception')) {
      try {
        const data = {
          request_for_exception: watch('request_for_exception'),
          request_for_exception_reason: watch('request_for_exception_reason'),
          decision_id: 3,
          decision_reason_id: 5,
          exception_reason: watch('exception_reason') || [],
        }
        await editEntityDataEmbeddedAPI(
          task?.identifier?.entity_name ?? '',
          watch('id') ?? '',
          data,
          [
            'request_for_exception',
            'request_for_exception_reason',
            'decision_id',
            'decision_reason_id',
            'exception_reason',
          ]
        )
        const caseData = {
          case_status_id:
            task?.identifier?.pap_review_exception_status_id ?? '',
        }
        await editEntityDataEmbeddedAPI(
          workflowDefinition?.global_variables?.entity_name ?? '',
          workflowInstance?.case_id ?? caseId ?? '',
          caseData,
          ['users', 'case_status_id']
        )
        // dispatch(
        //   addToaster({
        //     type: 'success',
        //     message: TOASTER_NAMESPACE.CASE_MOVED_TO_EXCEPTION_STATUS,
        //     id: uuid(),
        //   })
        // ) as discussed with sourabh
        return true
      } catch (err) {
        console.log(err)
        return false
      }
    } else return true
  }

  const handleReloadButtonClick = async () => {
    try {
      dispatch(showLoader())
      await executeCustomProcedure(
        task?.identifier?.custom_procedure_name ?? '',
        workflowInstance?.case_id ?? '',
        watch('id') ?? ''
      )
      await fetchOutcomeDetails()
    } catch (err) {
      console.error(err)
    } finally {
      dispatch(hideLoader())
    }
  }
  const fetchExceptionOptions = async () => {
    try {
      const response = await getExceptionReasonsOptions()
      if (response?.data) {
        const exceptionField = response?.data?.fields?.find(
          (item: IEntityFieldType) => item?.name === STRINGS.EXCEPTION_REASON
        )
        setexceptionReasons(
          exceptionField?.control_values
            ?.map((str: string) => ({
              label: str,
              value: str,
            }))
            ?.sort((a: DropdownType<string>, b: DropdownType<string>) =>
              a?.label?.localeCompare(b?.label)
            ) || []
        )
      }
    } catch (err) {
      console.error(err)
    }
  }
  useEffect(() => {
    const ishidden: SiteSettingsType =
      siteSettings?.find(
        (item: SiteSettingsType) =>
          item?.key === PAP_WORKFLOW_CONSTANTS.HIDE_EXCEPTION_CHECKBOX
      ) ?? {}
    setIsHiddenException((ishidden?.value as boolean) ?? false)
  }, [siteSettings])

  useEffect(() => {
    fetchExceptionOptions()
    if (task?.resource_id) fetchOutcomeDetails()
    uiIdentifierRef.current = {
      ...uiIdentifierRef.current,
      [task?.id ?? '']: {
        updateCaseData: updateCaseDetails,
      },
    }
  }, [])

  if (task?.resource_id) {
    return (
      <form>
        <div className="border rounded-b border-accent-100 p-3 layout flex flex-col">
          <Accordion
            alwaysOpen={true}
            heading={
              <div className="flex justify-between">
                <span>Results</span>
                {task?.state === WORKFLOW_STATUS.OPEN ? (
                  <span
                    className="cursor-pointer flex p-1"
                    onClick={handleReloadButtonClick}
                  >
                    <SvgReload />
                  </span>
                ) : null}
              </div>
            }
          >
            <div className="p-3">
              <div className="grid grid-cols-3 gap-5">
                {layout
                  .filter((input) => input.visible !== false)
                  .map((input) => {
                    if (input.type === 'checkbox') {
                      return (
                        <div
                          key={input.name}
                          style={{
                            gridRowStart: input.row ?? undefined,
                            gridColumn: input.colSpan
                              ? `span ${input.colSpan}`
                              : '',
                          }}
                        >
                          <Checkbox
                            tabIndex={0}
                            name={input.name}
                            label={input.label}
                            dataQaId={`requestor-search-${input.name}`}
                            register={register}
                            onChange={(e) => {
                              setShowRequestForExceptionReason(e.target.checked)
                            }}
                            disabled={!input.isEditable}
                          />
                        </div>
                      )
                    }
                    if (
                      input.type === 'MultiSelectDropdown' &&
                      input.isEditable
                    ) {
                      return (
                        <div
                          className="flex-1"
                          key={input.name}
                          style={{
                            gridRowStart: input.row ?? undefined,
                            gridColumn: input.colSpan
                              ? `span ${input.colSpan}`
                              : '',
                          }}
                        >
                          <ControlledMultiSelect
                            options={exceptionReasons}
                            name={input.name}
                            label={input.label}
                            noOfChips={3}
                            control={control}
                            disabled={!input.isEditable}
                            required={false}
                            placeholder={'Select Options'}
                            onChange={(e: DropdownType<string>[]) => {
                              const values = (e || [])?.map((it) => it?.value)
                              setValue(input?.name, values)
                            }}
                            dataQaId={`eligibility-${input?.name}`}
                          />
                        </div>
                      )
                    }
                    if (input.isEditable) {
                      return (
                        <div
                          className="w-full min-w-[11.69rem]"
                          key={input.name}
                          style={{
                            gridRowStart: input.row ?? undefined,
                            gridColumn: input.colSpan
                              ? `span ${input.colSpan}`
                              : '',
                          }}
                        >
                          <FormInput
                            dataQaId={`requestor-search-${input.name}`}
                            label={input.label}
                            name={input.name}
                            type={'text'}
                            tabIndex={0}
                            register={register}
                            placeholder="--"
                          />
                        </div>
                      )
                    }
                    return (
                      <div
                        className="w-full min-w-[11.69rem]"
                        key={input?.name}
                        style={{
                          gridRowStart: input.row ?? undefined,
                          gridColumn: input.colSpan
                            ? `span ${input.colSpan}`
                            : '',
                        }}
                      >
                        <div className="font-normal break-words text-sm">
                          {input?.label}
                          {input?.required ? (
                            <span
                              className={`ml-1 text-xs leading-1 text-error-500`}
                            >
                              *
                            </span>
                          ) : null}
                        </div>
                        <div className="font-extralight text-xs min-h-4.5">
                          {input.name === 'exception_reason'
                            ? watch(input?.name)?.join(', ')
                            : watch(input?.name) ?? '--'}
                        </div>
                      </div>
                    )
                  })}
              </div>
            </div>
          </Accordion>
          <Accordion alwaysOpen={true} heading={<div>{'Outcome'}</div>}>
            <div className="p-2">
              <CustomTable
                columns={columns}
                data={outcomeData}
                emptyDataMessage="No Data"
                showTableColumnOnEmpty={true}
              />
            </div>
          </Accordion>
        </div>
      </form>
    )
  } else return null
}
